import { css } from "@emotion/react";
import {
  differenceInMilliseconds,
  differenceInMinutes,
  differenceInSeconds,
  format,
  endOfDay as getEndOfDay,
  startOfDay as getStartOfDay,
  isSameDay,
} from "date-fns";
import { range } from "lodash";
import { MouseEvent, useRef } from "react";

import { DesignToken, ShadowToken } from "@livechat/design-system-react-components";

import { loadingAnimation } from "components/skeleton/styled";
import { AgentActivityEventType, IAgentActivityEvent } from "store/views/reports";
import { withVar } from "themes/helpers";

import { formatDuration } from "../helpers/format-duration";
import { getAgentActivityDuration } from "../helpers/get-agent-activity-duration";
import { textMuted, textSmall } from "../styled/typography";

/* eslint-disable react/no-array-index-key */

const SECONDS_IN_DAY = 60 * 60 * 24;

const container = css`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 70px;
  margin-left: auto;
  margin-right: auto;
`;

const ticks = css`
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-grow: 1;
  flex-basis: 0;
`;

const minorTick = css`
  width: 1px;
  height: 4px;
  margin-bottom: 7px;
  background-color: ${withVar(DesignToken.ContentBasicDisabled)};
`;

const majorTick = css`
  width: 1px;
  height: 8px;
  margin-bottom: 7px;
  background-color: ${withVar(DesignToken.ContentBasicSecondary)};
`;

const chart = css`
  position: relative;
  flex-grow: 0;
  flex-basis: 6px;
`;

const chartEvents = css`
  position: relative;
  width: 100%;
  height: 6px;
  background-color: ${withVar(DesignToken.SurfaceTertiaryDefault)};
`;

const chatEventsAnimated = css`
  ${loadingAnimation("#9e9e9e", 0.2)};
`;

const chartPopover = css`
  position: absolute;
  display: none;
  z-index: 1;
  bottom: 21px;
  text-align: center;
  box-shadow: ${withVar(ShadowToken.PopOver)};

  &:after {
    top: 100%;
    left: 50%;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-top-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
    border-width: 8px;
    margin-left: -8px;
  }
`;

const chartPopoverHead = css`
  padding: 7px 0 8px;
  font-size: 13px;
  color: ${withVar(DesignToken.ContentBasicSecondary)};
  border-radius: 4px 4px 0 0;
  background-color: ${withVar(DesignToken.SurfaceTertiaryDefault)};
`;

const chartPopoverBody = css`
  padding: 10px 14px 12px;
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  color: ${withVar(DesignToken.ContentBasicPrimary)};
  border-radius: 0 0 4px 4px;
`;

const event = css`
  position: absolute;
  top: 0;
  left: -1px;
  height: 6px;
  min-width: 1px;

  &:before {
    position: absolute;
    content: " ";
    top: -4px;
    bottom: -4px;
    left: 0;
    right: 0;
    background-color: transparent;
    pointer-events: auto;
  }
`;

const summary = css`
  flex-grow: 1;
  flex-basis: 0;
  display: flex;
  align-items: center;
`;

const onlineTimeLabel = css`
  display: inline-block;
  min-width: 170px;
`;

// @ts-expect-error TS(6133): 'v' is declared but its value is never read.
const TicksItems = range(25).map((v, i) => <div key={i} css={i % 4 === 0 ? majorTick : minorTick} />);

interface IProps {
  isFetching: boolean;
  date: Date;
  events: IAgentActivityEvent[] | null;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function AgentActivityChart(props: IProps) {
  const events = props.events || [];
  const popoverRef = useRef<HTMLDivElement>();
  const popoverHeadRef = useRef<HTMLDivElement>();
  const popoverBodyRef = useRef<HTMLDivElement>();
  const startOfDay = getStartOfDay(props.date);
  const endOfDay = getEndOfDay(props.date);
  const { onlineTime, offlineTime } = getAgentActivityDuration(events, startOfDay, endOfDay);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function getActivityEventStyle(activityEvent: IAgentActivityEvent) {
    const shift = differenceInSeconds(activityEvent.start, startOfDay);
    let duration = differenceInSeconds(activityEvent.end, activityEvent.start);

    // Protection from events start day before current date
    const startTime = Math.max(0, shift);

    // Remote time from previous day(-s)
    if (shift < 0) {
      duration += shift;
    }

    // Protection from duration excited day limit
    duration = Math.min(SECONDS_IN_DAY - startTime, duration);

    const left = (startTime / SECONDS_IN_DAY) * 100;
    const width = (duration / SECONDS_IN_DAY) * 100;
    const color = withVar(
      activityEvent.type === AgentActivityEventType.Online
        ? DesignToken.ActionPositiveDefault
        : DesignToken.ActionNegativeDefault
    );

    return {
      left: `${left}%`,
      width: `${width}%`,
      backgroundColor: color,
    };
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function getFormattedRange(activityEvent: IAgentActivityEvent) {
    const start = isSameDay(activityEvent.start, startOfDay) ? activityEvent.start : startOfDay;
    const end = isSameDay(activityEvent.end, endOfDay) ? activityEvent.end : endOfDay;
    const diffInMinutes = differenceInMinutes(end, start);
    let timeFormat = "hh:mm a";

    // For events shorted that minute show seconds as well
    if (diffInMinutes <= 1) {
      timeFormat = "hh:mm:ss a";
    }

    return `${format(start, timeFormat)} - ${format(end, timeFormat)}`;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function getFormatedDuration(activityEvent: IAgentActivityEvent) {
    const start = isSameDay(activityEvent.start, startOfDay) ? activityEvent.start : startOfDay;
    const end = isSameDay(activityEvent.end, endOfDay) ? activityEvent.end : endOfDay;
    const duration = differenceInMilliseconds(end, start);
    const label = activityEvent.type === AgentActivityEventType.Online ? "Accepting chats: " : "Not accepting chats: ";

    return `${label}<strong>${formatDuration(duration)}</strong>`;
  }

  /**
   * Show popover for hovered activity event
   */
  function handleMouseEnter(activityEvent: IAgentActivityEvent) {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return function (mouseEvent: MouseEvent<HTMLDivElement>) {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      popoverHeadRef.current.innerText = getFormattedRange(activityEvent);
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      popoverBodyRef.current.innerHTML = getFormatedDuration(activityEvent);

      // Show popover
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      popoverRef.current.style.display = "block";

      // calculate offset for position
      const target = mouseEvent.currentTarget;
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      const popoverLeft = target.offsetLeft + target.offsetWidth / 2 - popoverRef.current.offsetWidth / 2;

      // Update popover position
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      popoverRef.current.style.left = `${popoverLeft}px`;
    };
  }

  /**
   * Hide popover
   */
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function handleMouseLeave() {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    popoverRef.current.style.display = "none";
  }

  return (
    <div css={container}>
      <div css={ticks}>{TicksItems}</div>
      <div css={chart}>
        <div css={[chartEvents, props.isFetching && chatEventsAnimated]}>
          {events.map((v, index) => (
            <div
              key={index}
              css={event}
              style={getActivityEventStyle(v)}
              onMouseEnter={handleMouseEnter(v)}
              onMouseLeave={handleMouseLeave}
            />
          ))}
        </div>
        {/* @ts-expect-error TS(2322): Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message */}
        <div css={chartPopover} ref={popoverRef}>
          {/* @ts-expect-error TS(2322): Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message */}
          <div css={chartPopoverHead} ref={popoverHeadRef} />
          {/* @ts-expect-error TS(2322): Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message */}
          <div css={chartPopoverBody} ref={popoverBodyRef} />
        </div>
      </div>
      <div css={summary}>
        <span css={[textMuted, textSmall, onlineTimeLabel]}>
          accepting chats: <strong>{onlineTime === 0 ? "-" : formatDuration(onlineTime)}</strong>
        </span>
        <span css={[textMuted, textSmall]}>
          not accepting chats: <strong>{offlineTime === 0 ? "-" : formatDuration(offlineTime)}</strong>
        </span>
      </div>
    </div>
  );
}

export default AgentActivityChart;
